import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import {
  Button,
  DataTable,
  FilterBar,
  Kpi,
  Pager,
  Select,
  StateBlock,
  type Column,
  type DateRangePresetId,
} from '@w7/ui'
import { cmApi } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import { CmPanel } from '@/features/catalog/CmPanel'
import { RangePicker, presetOf } from '@/features/reports/RangePicker'
import { isoDay, money } from '@/lib/format'
import './vendor-dashboard.css'

/** Lab Config.maxRow for Instant Games Vendor Dashboard. */
const PAGE_SIZE = 20

type OperatorType = 'INR' | 'POINTS'

type Row = {
  siteUrl: string
  createdAt: string
  playerName: string
  subGame: string
  marketId: string
  type: string
  currency: string
  netAmount: number
  settlementId: string
}

type Applied = {
  from: string
  to: string
  operator: OperatorType
  run: number
}

function moneyCell(n: number) {
  const cls = n > 0 ? 'cm-money-pos' : n < 0 ? 'cm-money-neg' : undefined
  return <span className={cls}>{money(n, { symbol: false })}</span>
}

/**
 * Vendor Dashboard → Instant Games from Strix BQ settlement.settlement
 * (CM-API `POST /v1/vendor/instant-games/bq-detail`).
 * Chrome matches Betfair: FilterBar desk + RangePicker + KPI below + table panel.
 */
export function InstantGamesPage() {
  const today = isoDay(new Date())
  const [operator, setOperator] = useState<OperatorType>('INR')
  const [fromDate, setFromDate] = useState(today)
  const [toDate, setToDate] = useState(today)
  const [rangePreset, setRangePreset] = useState<DateRangePresetId>(() =>
    presetOf({ from: today, to: today }),
  )
  const [applied, setApplied] = useState<Applied>({
    from: today,
    to: today,
    operator: 'INR',
    run: 0,
  })
  const [page, setPage] = useState(1)

  function applyDetails(next?: {
    from: string
    to: string
    operator?: OperatorType
  }) {
    const from = next?.from ?? fromDate
    const to = next?.to ?? toDate
    const op = next?.operator ?? operator
    setPage(1)
    setApplied((prev) => ({
      from,
      to,
      operator: op,
      run: prev.run + 1,
    }))
  }

  const q = useQuery({
    queryKey: [
      'cm',
      'vendor',
      'instant-games',
      'bq-detail',
      applied.from,
      applied.to,
      applied.operator,
      applied.run,
      page,
      PAGE_SIZE,
    ],
    queryFn: () =>
      cmApi.instantGamesBqDetail(authHeaders(), {
        fromDate: applied.from,
        toDate: applied.to,
        operatorType: applied.operator,
        page,
        pageSize: PAGE_SIZE,
      }),
  })

  const rows = q.data?.rows ?? []
  const total = q.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const finalTotal = q.data?.finalTotal ?? 0

  const columns = useMemo<Column<Row>[]>(
    () => [
      {
        id: 'n',
        header: 'No.',
        width: 56,
        numeric: true,
        cell: (_r, i) => String((page - 1) * PAGE_SIZE + i + 1),
      },
      {
        id: 'createdAt',
        header: 'Created At',
        cell: (r) =>
          r.createdAt ? new Date(r.createdAt).toLocaleString() : '—',
        sortValue: (r) => r.createdAt ?? '',
      },
      {
        id: 'player',
        header: 'Player Name',
        cell: (r) => r.playerName || '—',
        sortValue: (r) => r.playerName || '',
      },
      {
        id: 'game',
        header: 'Game Name',
        cell: (r) => r.subGame || '—',
        sortValue: (r) => r.subGame || '',
      },
      {
        id: 'market',
        header: 'Market Id',
        cell: (r) => r.marketId || '—',
        sortValue: (r) => r.marketId || '',
      },
      {
        id: 'type',
        header: 'Type',
        cell: (r) => r.type || '—',
        sortValue: (r) => r.type || '',
      },
      {
        id: 'net',
        header: 'Net Amount',
        numeric: true,
        cell: (r) => moneyCell(r.netAmount),
        sortValue: (r) => r.netAmount,
      },
    ],
    [page],
  )

  return (
    <CmPanel
      title="Instant Games"
      subtitle="Vendor Dashboard — Instant Games from BigQuery"
      wide
    >
      <div className="cm-vendor-betfair cm-vendor-instant-games">
        <div className="cm-vendor-betfair-toolbar">
          <FilterBar label="Instant Games filters">
            <div className="cm-vendor-betfair-toolbar-filters">
              <RangePicker
                preset={rangePreset}
                value={{ from: fromDate, to: toDate }}
                onChange={(next) => {
                  setRangePreset(next.preset)
                  setFromDate(next.from)
                  setToDate(next.to)
                  applyDetails({ from: next.from, to: next.to })
                }}
              />
              <Select
                aria-label="Operator Type"
                value={operator}
                onChange={(e) => setOperator(e.target.value as OperatorType)}
                options={[
                  { value: 'INR', label: 'INR' },
                  { value: 'POINTS', label: 'POINTS' },
                ]}
              />
              <div className="cm-vendor-betfair-toolbar-actions">
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => applyDetails()}
                >
                  Get Details
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  icon="refresh"
                  busy={q.isFetching}
                  aria-label="Refresh"
                  onClick={() => void q.refetch()}
                >
                  Refresh
                </Button>
              </div>
            </div>
          </FilterBar>
        </div>

        <div className="cm-vendor-filters-kpi cm-vendor-filters-kpi-pair cm-vendor-betfair-kpi">
          <Kpi
            label="Final Total:"
            value={q.isLoading && !q.data ? '—' : moneyCell(finalTotal)}
          />
        </div>
      </div>

      {q.isLoading && !rows.length ? (
        <StateBlock icon="sport" title="Loading Instant Games…">
          Reading Instant Games BQ settlements
        </StateBlock>
      ) : null}

      {q.isError ? (
        <StateBlock icon="warning" title="Could not load Instant Games">
          {q.error instanceof Error
            ? q.error.message
            : 'Instant Games detail request failed'}
        </StateBlock>
      ) : null}

      {!q.isLoading && !q.isError && rows.length === 0 ? (
        <StateBlock icon="search" title="No data found">
          No Instant Games BQ settlements for these filters (scoped to your CM
          company_master / agentId).
        </StateBlock>
      ) : null}

      {rows.length > 0 ? (
        <div className="cm-vendor-betfair-results cm-vendor-instant-games-results">
          <div className="cm-vendor-betfair-table-wrap">
            <DataTable
              label="Instant Games settlements"
              columns={columns}
              rows={rows}
              rowKey={(r, i) => `${r.settlementId}-${i}`}
              density="default"
              stickyOffset={0}
            />
          </div>
          <div className="cm-vendor-betfair-pager">
            <Pager
              page={page}
              pageCount={pageCount}
              total={total}
              pageSize={PAGE_SIZE}
              onChange={setPage}
            />
          </div>
        </div>
      ) : null}
    </CmPanel>
  )
}
